import {
  TEXT_COLOR_ON_DARK,
  TEXT_COLOR_ON_LIGHT,
  BACKGROUND_PRESETS,
  CUT_EDGE_HIGHLIGHT_COLORS,
  type BackgroundPreset,
  type ViewerPreferenceSettings,
} from '@trimble/viewer-preferences';
import { t } from '@trimble/i18n';
import { ColorPicker } from '@trimble/ui-components/solidjs';
import {
  colorsAreEqual,
  isDarkColor,
  rgbColorToCssHexString,
} from '@trimble/utilities';
import { computePosition, flip, offset, shift } from '@floating-ui/dom';
import {
  Component,
  createEffect,
  createMemo,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
  type Accessor,
  type Setter,
} from 'solid-js';
import { Portal } from 'solid-js/web';

const RAINBOW_GRADIENT =
  'conic-gradient(#ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)';

const PICKER_MARGIN = 8;

const SWATCH_BASE_CLASS =
  'flex h-7 w-7 cursor-pointer items-center justify-center rounded border border-trimble-gray-300 p-0';

const SWATCH_HOVER_CLASS = 'hover:ring-2 hover:ring-trimble-gray-350';

const SELECTED_CLASS = 'ring-2 ring-inset ring-primary';

const LIGHT_SWATCH_CHECK_CLASS = 'text-primary';
const DARK_SWATCH_CHECK_CLASS = 'text-white';

interface SwatchProps {
  background: string;
  selected: boolean;
  label: string;
  dataTestId: string;
  checkClass: string;
  onSelect(): void;
}

const ColorSwatch: Component<SwatchProps> = (props) => (
  <button
    type="button"
    role="radio"
    data-testid={props.dataTestId}
    aria-label={props.label}
    aria-checked={props.selected}
    title={props.label}
    class={`${SWATCH_BASE_CLASS} ${props.selected ? SELECTED_CLASS : SWATCH_HOVER_CLASS}`}
    style={{ background: props.background }}
    onClick={() => props.onSelect()}
  >
    <Show when={props.selected}>
      <i
        class={`icon-font tc-icon-check ${props.checkClass}`}
        aria-hidden="true"
      />
    </Show>
  </button>
);

export interface ViewerPreferencesSettingsProps {
  preferenceSettings: Accessor<ViewerPreferenceSettings>;
  setPreferenceSettings: Setter<ViewerPreferenceSettings>;
}

export const ViewerPreferencesSettings: Component<
  ViewerPreferencesSettingsProps
> = (props) => {
  const [showCustomPicker, setShowCustomPicker] = createSignal(false);
  const [pickerPosition, setPickerPosition] = createSignal({ top: 0, left: 0 });
  const [isPositioned, setIsPositioned] = createSignal(false);
  const [pickerElement, setPickerElement] = createSignal<HTMLDivElement>();
  let customSwatchRef: HTMLButtonElement | undefined;
  let positionRequestId = 0;

  const matchesPreset = (preset: BackgroundPreset): boolean => {
    const prefs = props.preferenceSettings();
    return (
      colorsAreEqual(prefs.backgroundTopColor, preset.topColor) &&
      colorsAreEqual(prefs.backgroundBottomColor, preset.bottomColor)
    );
  };

  const isCustomSelected = createMemo(
    () => !BACKGROUND_PRESETS.some(matchesPreset)
  );

  const selectBackground = (preset: BackgroundPreset): void => {
    props.setPreferenceSettings((prev) => ({
      ...prev,
      backgroundTopColor: preset.topColor,
      backgroundBottomColor: preset.bottomColor,
      textColor: preset.textColor,
      darkModeMaterials: preset.darkModeMaterials,
    }));
  };

  const selectCustomBackground = (hexColor: string): void => {
    const dark = isDarkColor(hexColor);
    props.setPreferenceSettings((prev) => ({
      ...prev,
      backgroundTopColor: hexColor,
      backgroundBottomColor: hexColor,
      textColor: dark ? TEXT_COLOR_ON_DARK : TEXT_COLOR_ON_LIGHT,
      darkModeMaterials: dark,
    }));
  };

  const selectHighlight = (color: string): void => {
    props.setPreferenceSettings((prev) => ({
      ...prev,
      clippingHighlightColor: color,
    }));
  };

  const openCustomPicker = (): void => {
    setIsPositioned(false);
    setShowCustomPicker(true);
  };

  const updatePickerPosition = (): void => {
    const floating = pickerElement();
    if (!showCustomPicker() || !floating || !customSwatchRef) {
      return;
    }
    const requestId = ++positionRequestId;
    void computePosition(customSwatchRef, floating, {
      strategy: 'fixed',
      placement: 'bottom-start',
      middleware: [
        offset(PICKER_MARGIN),
        flip(),
        shift({ padding: PICKER_MARGIN }),
      ],
    }).then(({ x, y }) => {
      if (requestId !== positionRequestId) {
        return;
      }
      setPickerPosition({ left: x, top: y });
      setIsPositioned(true);
    });
  };

  createEffect(() => {
    if (showCustomPicker() && pickerElement()) {
      updatePickerPosition();
    }
    onCleanup(() => {
      positionRequestId += 1;
    });
  });

  const handleClickOutside = (event: MouseEvent): void => {
    if (!showCustomPicker()) return;
    const target = event.target instanceof Node ? event.target : null;
    if (
      target &&
      (customSwatchRef?.contains(target) || pickerElement()?.contains(target))
    ) {
      return;
    }
    setShowCustomPicker(false);
  };

  onMount(() => {
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', updatePickerPosition);
    window.addEventListener('scroll', updatePickerPosition, true);
  });
  onCleanup(() => {
    document.removeEventListener('mousedown', handleClickOutside);
    window.removeEventListener('resize', updatePickerPosition);
    window.removeEventListener('scroll', updatePickerPosition, true);
  });

  const getCheckClass = (color: string): string =>
    isDarkColor(color) ? DARK_SWATCH_CHECK_CLASS : LIGHT_SWATCH_CHECK_CLASS;

  return (
    <div data-testid="preferences-settings" class="flex flex-col gap-4">
      <div class="flex flex-col gap-2">
        <span
          class="text-sm"
          textContent={t('mobileMapping.settings.backgroundColor')}
        />
        <div
          role="radiogroup"
          aria-label={t('mobileMapping.settings.backgroundColor')}
          class="flex flex-wrap gap-2"
        >
          <For each={BACKGROUND_PRESETS}>
            {(preset) => (
              <ColorSwatch
                background={`linear-gradient(to bottom, ${preset.topColor}, ${preset.bottomColor})`}
                label={preset.id}
                dataTestId={`background-swatch-${preset.id}`}
                selected={matchesPreset(preset)}
                checkClass={getCheckClass(preset.topColor)}
                onSelect={() => selectBackground(preset)}
              />
            )}
          </For>
          <button
            type="button"
            role="radio"
            ref={(element) => {
              customSwatchRef = element;
            }}
            data-testid="background-swatch-custom"
            aria-label={t('mobileMapping.settings.customColor')}
            aria-checked={isCustomSelected()}
            aria-haspopup="dialog"
            title={t('mobileMapping.settings.customColor')}
            class={`${SWATCH_BASE_CLASS} ${isCustomSelected() ? SELECTED_CLASS : SWATCH_HOVER_CLASS}`}
            style={{
              background: isCustomSelected()
                ? props.preferenceSettings().backgroundTopColor
                : RAINBOW_GRADIENT,
            }}
            onClick={openCustomPicker}
          >
            <Show when={isCustomSelected()}>
              <i
                class={`icon-font tc-icon-check ${getCheckClass(
                  props.preferenceSettings().backgroundTopColor
                )}`}
                aria-hidden="true"
              />
            </Show>
          </button>
        </div>
      </div>

      <Show when={showCustomPicker()}>
        <Portal>
          <div
            ref={(element) => setPickerElement(element)}
            class="fixed z-dropdown"
            style={{
              top: `${pickerPosition().top}px`,
              left: `${pickerPosition().left}px`,
              visibility: isPositioned() ? 'visible' : 'hidden',
            }}
            data-testid="background-color-picker"
          >
            <ColorPicker
              cssAlignment=""
              initialColor={props.preferenceSettings().backgroundTopColor}
              onSave={(rgb) => {
                selectCustomBackground(rgbColorToCssHexString(rgb));
                setShowCustomPicker(false);
              }}
            />
          </div>
        </Portal>
      </Show>

      <div class="flex flex-col gap-2">
        <span
          class="text-sm"
          textContent={t('mobileMapping.settings.cutEdgeHighlightColor')}
        />
        <div
          role="radiogroup"
          aria-label={t('mobileMapping.settings.cutEdgeHighlightColor')}
          class="flex flex-wrap gap-2"
        >
          <For each={CUT_EDGE_HIGHLIGHT_COLORS}>
            {(color) => (
              <ColorSwatch
                background={color}
                label={color}
                dataTestId={`highlight-swatch-${color}`}
                selected={colorsAreEqual(
                  props.preferenceSettings().clippingHighlightColor,
                  color
                )}
                checkClass={getCheckClass(color)}
                onSelect={() => selectHighlight(color)}
              />
            )}
          </For>
        </div>
      </div>
    </div>
  );
};
